import { ExternalLink } from 'lucide-react'
import { motion } from 'motion/react'
import type { Experience, UiCopy } from '../content/types'
import { iconByKey } from '../lib/icons'
import { Gallery } from './Gallery'

interface ExperienceSectionProps {
  experience: Experience
  ui: UiCopy
}

export function ExperienceSection({ experience, ui }: ExperienceSectionProps) {
  const Icon = iconByKey[experience.icon]
  return (
    <motion.article
      className="experience-entry"
      id={`experience-${experience.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-12%' }}
      transition={{ duration: 0.52 }}
    >
      <div className="experience-main">
        <header className="experience-header">
          <span className="experience-icon"><Icon aria-hidden="true" /></span>
          <div className="experience-heading-copy">
            <p className="experience-period">{experience.period}</p>
            <h3>{experience.company}</h3>
            {experience.role && <p className="experience-role">{experience.role}</p>}
          </div>
        </header>
        <p className="experience-summary">{experience.summary}</p>

        {experience.products.length > 0 && (
          <div className="products-block">
            <h4>{ui.products}</h4>
            <div className="product-list">
              {experience.products.map((product) => <article key={product.name}><h5>{product.name}</h5><p>{product.description}</p></article>)}
            </div>
          </div>
        )}

        <details className="experience-details">
          <summary><span className="show-label">{ui.showDetails}</span><span className="hide-label">{ui.hideDetails}</span></summary>
          <div className="details-content"><h4>{ui.details}</h4><ul>{experience.details.map((detail) => <li key={detail}>{detail}</li>)}</ul></div>
        </details>

        {experience.media.length > 0 && <Gallery items={experience.media} ui={ui} />}
      </div>
      <aside className="experience-aside">
        <h4>{ui.technologies}</h4>
        <div className="tech-list">{experience.technologies.map((technology) => <span key={technology}>{technology}</span>)}</div>
        {experience.website && <a href={experience.website} target="_blank" rel="noreferrer">{ui.visitWebsite}<ExternalLink size={15} /></a>}
      </aside>
    </motion.article>
  )
}
